import {type FeedbackRef, NewUserFeedback} from '@github-ui/user-feedback/NewUserFeedback'
import {ThumbsdownIcon, ThumbsupIcon} from '@primer/octicons-react'
import {forwardRef} from 'react'

import {useChatManager} from '../utils/CopilotChatManagerContext'
import styles from './ResponseFeedbackDialog.module.css'

const ratingOptions = [
  {
    label: 'bad' as const,
    icon: <ThumbsdownIcon className={styles.thumbsDown} />,
    description: 'Bad',
    value: 'NEGATIVE' as const,
  },
  {
    label: 'good' as const,
    icon: <ThumbsupIcon className={styles.thumbsUp} />,
    description: 'Good',
    value: 'POSITIVE' as const,
  },
]

type Rating = (typeof ratingOptions)[number]['value']
export type FeedbackDialogRef = FeedbackRef<Rating>

type ResponseFeedbackDialogProps = {
  messageId: string
  threadId: string
  onSubmitted?: (rating: Rating) => void
  onClose?: () => void
}

function ResponseFeedbackDialogInner(
  {messageId, threadId, onSubmitted, onClose}: ResponseFeedbackDialogProps,
  ref: React.ForwardedRef<FeedbackDialogRef>,
) {
  const manager = useChatManager()

  const onSubmit = async (rating: Rating | null, text: string): Promise<string[]> => {
    const errors = []

    if (rating == null) {
      errors.push('Please select a rating.')
    }

    // max length based on hydro payload size limit
    // see https://github.com/github/copilot-api/blob/62323cfa4bfc31f808f8a457644f11bb706b8298/pkg/rest/chat.go#L382-L384
    const limit = 500
    if (text.length > limit) {
      errors.push(`Please keep your feedback within ${limit} characters or less.`)
    }

    if (errors.length > 0) {
      return errors
    } else {
      rating = rating! // Make TS happy
    }

    try {
      const response = await manager.service.sendFeedback({
        feedback: rating,
        feedbackChoice: [], // This component does not offer users the ability to choose these (e.g. "UNHELPFUL", "INCORRECT", "NOT_TRUE", "POORLY_FORMATTED", etc.)
        messageId,
        threadId,
        textResponse: text,
      })

      if (response.ok) {
        onSubmitted?.(rating)
      } else {
        errors.push('An error occurred while submitting your feedback.')
      }
    } catch {
      errors.push('An error occurred while submitting your feedback.')
    }

    return errors
  }

  return (
    <NewUserFeedback
      title="Give additional feedback"
      ref={ref}
      options={ratingOptions}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  )
}

export const ResponseFeedbackDialog = forwardRef(ResponseFeedbackDialogInner)

try{ ResponseFeedbackDialogInner.displayName ||= 'ResponseFeedbackDialogInner' } catch {}
try{ ResponseFeedbackDialog.displayName ||= 'ResponseFeedbackDialog' } catch {}